import { useState, useEffect } from 'react';
import { LockClosedIcon, LockOpenIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../../lib/supabase';

export default function MobileSettingsRegistration() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [reservedSpots, setReservedSpots] = useState(0);
  const [capacityLoading, setCapacityLoading] = useState(true);
  const [capacitySaving, setCapacitySaving] = useState(false);
  const [totalCapacity, setTotalCapacity] = useState(250);
  const [currentRegistrations, setCurrentRegistrations] = useState(0);

  useEffect(() => {
    fetchSettings();
    fetchCapacityData();
  }, []);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const { data } = await supabase
        .from('system_settings')
        .select('key, value')
        .eq('key', 'registration_enabled');

      if (data) {
        const enabled = data.find(s => s.key === 'registration_enabled');
        setIsEnabled(enabled?.value === true || enabled?.value === 'true');
      }
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCapacityData = async () => {
    try {
      setCapacityLoading(true);

      const { data: settingsData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'reserved_spots')
        .maybeSingle();

      if (settingsData) {
        const v = settingsData.value;
        const parsed = typeof v === 'number' ? v : parseInt(String(v));
        setReservedSpots(isNaN(parsed) ? 0 : parsed);
      }

      const { data: tournamentData } = await supabase
        .from('tournaments')
        .select('id, capacity')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(1)
        .maybeSingle();

      const capacity = tournamentData?.capacity ?? 250;
      const tournamentId = tournamentData?.id ?? null;
      setTotalCapacity(capacity);

      let countQuery = supabase.from('players').select('*', { count: 'exact', head: true });
      if (tournamentId) countQuery = countQuery.eq('tournament_id', tournamentId);
      const { count } = await countQuery;
      if (count !== null) setCurrentRegistrations(count);
    } catch {
      toast.error('Failed to load capacity data');
    } finally {
      setCapacityLoading(false);
    }
  };

  const toggleEnabled = async () => {
    const next = !isEnabled;
    setIsSaving(true);
    try {
      await supabase.from('system_settings').upsert(
        { key: 'registration_enabled', value: next, category: 'registration', description: 'Whether registration is currently open', is_public: true },
        { onConflict: 'key' }
      );
      setIsEnabled(next);
      toast.success(`Registration ${next ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update registration status');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCapacity = async () => {
    setCapacitySaving(true);
    try {
      const { data: existing } = await supabase
        .from('system_settings')
        .select('id')
        .eq('key', 'reserved_spots')
        .maybeSingle();

      if (existing) {
        await supabase
          .from('system_settings')
          .update({ value: reservedSpots, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
      } else {
        await supabase.from('system_settings').insert([{
          key: 'reserved_spots',
          value: reservedSpots,
          category: 'registration',
          description: 'Number of spots reserved for special registrations',
          is_public: true,
        }]);
      }
      toast.success('Reserved spots updated');
    } catch {
      toast.error('Failed to update reserved spots');
    } finally {
      setCapacitySaving(false);
    }
  };

  const availableSpots = totalCapacity - reservedSpots - currentRegistrations;
  const registeredPct = (currentRegistrations / totalCapacity) * 100;
  const reservedPct = (reservedSpots / totalCapacity) * 100;
  const availablePct = Math.max(0, 100 - registeredPct - reservedPct);
  const maxReserved = Math.max(0, totalCapacity - currentRegistrations);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-golf-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isEnabled ? 'bg-green-50' : 'bg-gray-100'}`}>
              {isEnabled
                ? <LockOpenIcon className="w-5 h-5 text-green-500" />
                : <LockClosedIcon className="w-5 h-5 text-gray-400" />}
            </div>
            <div>
              <p className="text-sm font-semibold text-golf-gray">Registration</p>
              <p className={`text-xs font-medium ${isEnabled ? 'text-green-500' : 'text-gray-400'}`}>
                {isEnabled ? 'Enabled' : 'Disabled'}
              </p>
            </div>
          </div>
          <button
            onClick={toggleEnabled}
            disabled={isSaving}
            className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${isEnabled ? 'bg-golf-orange' : 'bg-gray-200'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${isEnabled ? 'translate-x-6' : 'translate-x-0'}`} />
          </button>
        </div>
        {isEnabled && (
          <p className="text-xs text-green-600 mt-3 flex items-center gap-1.5">
            <CheckCircleIcon className="w-3.5 h-3.5" />
            Players can register for the tournament
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <p className="text-sm font-semibold text-golf-gray mb-4">Registration Capacity Management</p>

        {capacityLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-golf-orange border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { label: 'Total', value: totalCapacity },
                { label: 'Registered', value: currentRegistrations },
                { label: 'Available', value: availableSpots },
              ].map(stat => (
                <div key={stat.label} className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-golf-gray">{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-medium text-gray-700">Reserved Spots</p>
                <span className="text-xs font-semibold text-golf-orange">{reservedSpots}</span>
              </div>
              <input
                type="range"
                min={0}
                max={maxReserved}
                value={reservedSpots}
                onChange={e => setReservedSpots(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-golf-orange"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0</span>
                <span>{maxReserved}</span>
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <input
                type="number"
                min={0}
                max={maxReserved}
                value={reservedSpots}
                onChange={e => {
                  const v = parseInt(e.target.value);
                  if (!isNaN(v) && v >= 0 && v <= maxReserved) setReservedSpots(v);
                }}
                className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-golf-orange"
              />
              <button
                onClick={handleSaveCapacity}
                disabled={capacitySaving}
                className="px-4 py-2 bg-golf-orange text-white text-sm font-medium rounded-xl hover:bg-golf-orange/90 transition-colors disabled:opacity-50"
              >
                {capacitySaving ? 'Saving…' : 'Save'}
              </button>
            </div>

            <div className="mb-4">
              <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden flex">
                <div className="bg-green-500 h-full transition-all" style={{ width: `${registeredPct}%` }} />
                <div className="bg-gray-400 h-full transition-all" style={{ width: `${reservedPct}%` }} />
                <div className="bg-blue-400 h-full transition-all" style={{ width: `${availablePct}%` }} />
              </div>
              <div className="flex justify-between mt-2 gap-2">
                {[
                  { color: 'bg-green-500', label: `Registered (${currentRegistrations})` },
                  { color: 'bg-gray-400', label: `Reserved (${reservedSpots})` },
                  { color: 'bg-blue-400', label: `Available (${availableSpots})` },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-1.5">
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${item.color}`} />
                    <span className="text-xs text-gray-500">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-2">
              <InformationCircleIcon className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700 leading-relaxed">
                Reserved spots are held for VIP players, sponsors, or officials and are not available for general registration.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
